import React, { useState, useEffect, useCallback } from 'react'
import moment from 'moment'
import { connect } from 'react-redux'
import { showNotification } from 'react-admin'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import Slide from '@material-ui/core/Slide'
import Button from '@material-ui/core/Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/free-solid-svg-icons'

import { OfferBoardTabs } from '../../components/offerBoard/OfferBoardTabs/OfferBoardTabs'
import { OfferBoardDetails } from '../../components/offerBoard/OfferBoardDetails/OfferBoardDetails'
import { OfferBoardCreate } from './create/model'
import './styles.css'
import KindFetch from './kindFetch'
import offerBoardService from './offerBoardService'

const formatDate = (value) => value ? moment(value).format('DD/MM/YYYY') : '-'

const boardIdOf = (board) => board && (board.id || board._id)

const boardName = (board) =>
  `${board.searchNumber ? `${board.searchNumber} - ` : ''}${board.title || 'Offer Board'}`

function Transition (props) {
  return <Slide direction='up' {...props} />
}

// The Offer Boards are set up from this page. Several boards run at the same
// time: each one is listed here and opened to see its details and its lots, and
// "Add Offer Board" sets up another. While there is no board at all the create
// form is shown straight away.
//
// A board is finished with by archiving it. It then comes off the webapp and
// moves down to the Archived section, where it is opened and read back as it
// was, listings and offers and all.
export const OfferBoardListView = (props) => {
  let { showNotification } = props
  const [offerBoards, setOfferBoards] = useState([])
  const [archivedBoards, setArchivedBoards] = useState([])
  const [openBoard, setOpenBoard] = useState(null)
  const [openArchived, setOpenArchived] = useState(null)
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [boardToArchive, setBoardToArchive] = useState(null)
  const { kinds } = KindFetch()

  const loadOfferBoards = useCallback(async () => {
    let boards = []
    try {
      boards = await offerBoardService.getOfferBoards()
    } catch (err) {
      boards = []
    }
    setOfferBoards(boards)
    // the newest board is opened to start with, the way the page showed it when
    // there was only ever one
    setOpenBoard(boards.length ? boardIdOf(boards[0]) : null)
    setLoading(false)
  }, [])

  const loadArchivedBoards = useCallback(async () => {
    try {
      setArchivedBoards(await offerBoardService.getArchivedOfferBoards())
    } catch (err) {
      setArchivedBoards([])
    }
  }, [])

  useEffect(() => {
    loadOfferBoards()
    loadArchivedBoards()
  }, [loadOfferBoards, loadArchivedBoards])

  // a new board goes to the top of the list, opened
  const onCreated = (saved) => {
    setOfferBoards((boards) => [saved, ...boards])
    setOpenBoard(boardIdOf(saved))
    setAdding(false)
  }

  const onUpdated = (saved) => {
    setOfferBoards((boards) => boards.map((board) =>
      boardIdOf(board) === boardIdOf(saved) ? saved : board))
    setEditingId(null)
  }

  // the board is gone, it is taken off the list
  const onDeleted = (boardId) => {
    setOfferBoards((boards) => boards.filter((board) => boardIdOf(board) !== boardId))
    setEditingId(null)
    if (openBoard === boardId) {
      setOpenBoard(null)
    }
  }

  const archive = async () => {
    const board = boardToArchive
    setBoardToArchive(null)
    if (!board) {
      return
    }
    const boardId = boardIdOf(board)
    try {
      await offerBoardService.archiveOfferBoard(boardId)
      showNotification('Offer Board was archived', 'success')
      setOfferBoards((boards) => boards.filter((one) => boardIdOf(one) !== boardId))
      if (editingId === boardId) {
        setEditingId(null)
      }
      if (openBoard === boardId) {
        setOpenBoard(null)
      }
      await loadArchivedBoards()
    } catch (err) {
      showNotification(offerBoardService.errorMessage(err, 'Offer Board could not be archived'), 'warning')
    }
  }

  const startAdding = () => {
    setEditingId(null)
    setAdding(true)
  }

  // with no board at all there is nothing to go back to, so the create form
  // stays and has no Cancel
  const renderCreate = () => {
    if (loading) {
      return null
    }
    if (!adding && offerBoards.length) {
      return null
    }
    return (
      <OfferBoardCreate
        offerBoard={null}
        onSaved={onCreated}
        onCancel={offerBoards.length ? () => setAdding(false) : null} />
    )
  }

  const renderBoardBody = (board) => {
    const boardId = boardIdOf(board)
    if (editingId === boardId) {
      return (
        <OfferBoardCreate
          offerBoard={board}
          onSaved={onUpdated}
          onDeleted={() => onDeleted(boardId)}
          onCancel={() => setEditingId(null)} />
      )
    }
    return (
      <>
        <OfferBoardDetails
          offerBoard={board}
          kinds={kinds}
          onEdit={() => {
            setAdding(false)
            setEditingId(boardId)
          }}
          onArchive={() => setBoardToArchive(board)} />
        <OfferBoardTabs offerBoard={board} />
      </>
    )
  }

  // one row per running board, opened to work with it and closed again
  const renderBoards = () => {
    if (loading) {
      return <p className='offer-board-loading'>Loading...</p>
    }
    if (!offerBoards.length) {
      return null
    }
    return (
      <div className='offer-board-list'>
        {offerBoards.map((board) => {
          const boardId = boardIdOf(board)
          const open = openBoard === boardId
          return (
            <div className='offer-board-list-item' key={boardId}>
              <button
                type='button'
                className='offer-board-list-row'
                onClick={() => setOpenBoard(open ? null : boardId)}>
                <span className='offer-board-list-name'>{boardName(board)}</span>
                <span className='offer-board-list-date'>
                  Created {formatDate(board.createdAt)}
                </span>
                <span className='offer-board-list-toggle'>{open ? 'Close' : 'Open'}</span>
              </button>
              {open &&
                <div className='offer-board-list-body'>
                  {renderBoardBody(board)}
                </div>}
            </div>
          )
        })}
      </div>
    )
  }

  // one row per archived board, opened to read it back and closed again
  const renderArchived = () => {
    if (loading || !archivedBoards.length) {
      return null
    }
    return (
      <div className='offer-board-archived'>
        <p className='offer-board-archived-title'>Archived Offer Boards</p>
        {archivedBoards.map((board) => {
          const boardId = boardIdOf(board)
          const open = openArchived === boardId
          return (
            <div className='offer-board-archived-item' key={boardId}>
              <button
                type='button'
                className='offer-board-archived-row'
                onClick={() => setOpenArchived(open ? null : boardId)}>
                <span className='offer-board-archived-name'>{boardName(board)}</span>
                <span className='offer-board-archived-date'>
                  Archived {formatDate(board.archivedAt)}
                </span>
                <span className='offer-board-archived-toggle'>{open ? 'Close' : 'Open'}</span>
              </button>
              {open &&
                <div className='offer-board-archived-body'>
                  <OfferBoardDetails offerBoard={board} kinds={kinds} />
                  <OfferBoardTabs offerBoard={board} readOnly />
                </div>}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className='offer-board-page'>
      <div className='offer-board-header'>
        <p className='page-title'>Offer Boards</p>
        {!loading && offerBoards.length > 0 && !adding &&
          <Button variant='contained' color='primary' className='offer-board-add' onClick={startAdding}>
            <FontAwesomeIcon icon={faPlus} />
            <span className='offer-board-add-text'>Add Offer Board</span>
          </Button>}
      </div>
      {renderCreate()}
      {renderBoards()}
      {renderArchived()}
      <Dialog
        open={Boolean(boardToArchive)}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => setBoardToArchive(null)}
        aria-labelledby='archive-offer-board-title'
        aria-describedby='archive-offer-board-description'
      >
        <DialogTitle id='archive-offer-board-title'>
          Archive {boardToArchive ? boardName(boardToArchive) : 'this Offer Board'}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='archive-offer-board-description'>
            It comes off the webapp. Its listings and its offers are kept and can be read back from the Archived section. The other Offer Boards are not affected.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={archive} color='primary'>
            Yes
          </Button>
          <Button onClick={() => setBoardToArchive(null)} color='primary'>
            No
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export const OfferBoardList = connect(null, { showNotification })(OfferBoardListView)
